import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { NotificationMessage } from "../shared/models/models";
import { Register } from "../customer/shared/models/customerModels";
import customersApi from "../customer/shared/api/customersApi";
import { SerializedError } from "../shared/models/errorHandlingModels";
import { serializeError, createErrorNotification } from "../shared/utils/errorUtils";
import { isRegistrationHostRefusal } from "./pilotHostGate";

interface StartRegistrationState {
  isLoading: boolean;
  isConfirmEmail: boolean;
  formNotificationMessage: NotificationMessage | null;
  email: string | null;

  /**
   * COM-REGISTRATION-INVITE-ONLY (HOST_POLICY_SPEC §6 FE-B) — the server refused company
   * self-registration on this host (403 `REGISTRATION_NOT_AVAILABLE_ON_HOST`). Only reachable when
   * the client-side host check did not catch it first (e.g. a `.com` hostname added to the server's
   * `HostPolicy:ComHosts` but not yet to `COM_APP_HOSTS`), so the SERVER's word is what this flag
   * carries. The page renders the invite-only content instead of a red form error.
   */
  registrationNotAvailableOnHost: boolean;
}

const initialState: StartRegistrationState = {
  isLoading: false,
  isConfirmEmail: false,
  formNotificationMessage: null,
  email: null,
  registrationNotAvailableOnHost: false,
};

export const startRegistration = createAsyncThunk(
  "startRegistration/startRegistration",
  async (model: Register, { rejectWithValue }) => {
    try {
      await customersApi.register(model);
      return { success: true, email: model.userName };
    } catch (error: any) {
      return rejectWithValue(serializeError(error));
    }
  }
);

const startRegistrationSlice = createSlice({
  name: "startRegistration",
  initialState,
  reducers: {
    clearNotification: (state) => {
      state.formNotificationMessage = null;
    },
    resetState: (state) => {
      state.isLoading = false;
      state.isConfirmEmail = false;
      state.formNotificationMessage = null;
      state.email = null;
      state.registrationNotAvailableOnHost = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startRegistration.pending, (state) => {
        state.isLoading = true;
        state.formNotificationMessage = null;
      })
      .addCase(startRegistration.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isConfirmEmail = true;
        state.email = action.payload.email;
      })
      .addCase(startRegistration.rejected, (state, action) => {
        state.isLoading = false;

        const error = action.payload as SerializedError;
        if (isRegistrationHostRefusal(error?.code)) {
          // Not a form error — the whole PAGE is wrong for this host. Suppress the red alert and
          // let the page swap itself for the invite-only content.
          state.registrationNotAvailableOnHost = true;
          state.formNotificationMessage = null;
          return;
        }

        state.formNotificationMessage = createErrorNotification(error);
      });
  },
});

export const { clearNotification, resetState } = startRegistrationSlice.actions;
export default startRegistrationSlice.reducer; 